/**
 * Projects open in a tab of their own.
 *
 * Asked for by Huanrui on 2026-09-30: every way into a project -- a line typed
 * on the overview, the New project card, a project tile, "Create a project" on
 * the generate page -- opens a new tab, so the page it was started from stays
 * where it was.
 */

/** Open a project that exists (or the blank new one) in a new tab. */
export function openProjectTab(path: string): void {
  window.open(path, '_blank', 'noopener')
}

/**
 * A tab opened now, pointed at a project once it exists.
 *
 * A browser only lets a page open a tab in direct answer to a click; one
 * opened after waiting for the server is treated as a popup and blocked. So
 * the tab is opened at the click, blank, and sent to the project when it is
 * ready -- or closed again if the project could not be made.
 */
export function openProjectLater(): { go: (path: string) => void; cancel: () => void } {
  const tab = window.open('about:blank', '_blank')
  return {
    go(path) {
      if (tab && !tab.closed) {
        tab.opener = null
        tab.location.href = new URL(path, window.location.origin).toString()
      } else {
        // Blocked after all, or closed by hand: open it now instead.
        window.open(path, '_blank', 'noopener')
      }
    },
    cancel() {
      if (tab && !tab.closed) tab.close()
    },
  }
}

const OPENING = 'ww.opening.'

/**
 * Carry the line typed on the overview to the new tab.
 *
 * Router state does not cross tabs, and a prompt can be too long and too
 * private for the address bar, so it waits in this browser's storage under a
 * one-off key that the new tab reads and removes.
 */
export function handOverOpening(prompt: string): string {
  const key = Math.random().toString(36).slice(2, 10)
  try {
    window.localStorage.setItem(OPENING + key, prompt)
  } catch (error) {
    console.warn('could not hand the opening line to the new tab', error)
  }
  return key
}

/** The line handed over under this key, if there is one. Read, not removed. */
export function readOpening(key: string | null): string {
  if (!key) return ''
  try {
    return window.localStorage.getItem(OPENING + key) ?? ''
  } catch {
    return ''
  }
}

/** Once it has been sent. */
export function forgetOpening(key: string | null): void {
  if (!key) return
  try {
    window.localStorage.removeItem(OPENING + key)
  } catch (error) {
    console.warn('could not tidy the opening line away', error)
  }
}
